import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import isEmpty from "lodash/isEmpty";
import * as Yup from "yup";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

import MainLayout from "../../themes/layouts/Main";
import Form from "../../components/Projects/Edit";
import Loader from "../../themes/components/Loader";
import { notify, notifySimple } from "../../themes/components/Notifications";
import { fetchProject, updateProject, getAllPms } from "../../api/Project";
import { AccountContext } from "../../contexts/CurrentUser";

const validationSchema = Yup.object().shape({
  input: Yup.object().shape({
    name: Yup.string().min(2, "Too short!").max(255, "Too long!").required("Name is required"),
    completed_responses_limit: Yup.number()
      .min(1, "Completed Responses limit should be atleast 1")
      .required("Completed Responses limit is required"),
    suppliers: Yup.array().of(
      Yup.object().shape({
        score: Yup.number()
          .min(20, "Score Threshold must be at least 20")
          .max(100, "Score Threshold must be at most 100")
          .required("Score Threshold is required"),
        rt_proof_of_human_threshold: Yup.number()
          .min(20, "Roundtable AI Proof of Human Score Threshold must be at least 20")
          .max(100, "Roundtable AI Proof of Human Score Threshold must be at most 100")
          .required("Roundtable AI Proof of Human Score Threshold is required"),
      })
    ),
  }),
});

const ProjectEditRoute: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: Project["id"] }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ProjectData | null>(null);
  const [projectManagers, setProjectManagers] = useState<
    { id: string; first_name: string; last_name: string }[]
  >([]);
  const { account } = useContext(AccountContext);

  const fetchData = async () => {
    const result = await fetchProject(id);
    setLoading(result.loading);
    setError(result.error);
    setData(result.data);

    // Fetch project managers
    const pmResponse = await getAllPms();
    setProjectManagers(Array.isArray(pmResponse.result) ? pmResponse.result : []);
  };

  const onSubmit = async (values: ProjectUpdateVars) => {
    try {
      if (typeof id !== "string") {
        notify([
          {
            field: "id",
            message: "Parameter is missing",
          },
        ]);
        return;
      }

      const response = await updateProject(id, values);
      const { result, messages } = response.projectUpdate;

      if (!isEmpty(messages)) {
        notify(messages, "error");
      } else if (result) {
        notifySimple(["Project updated"], "success");
        navigate("/project/" + id + "/view");
      }
    } catch (err) {
      notifySimple(["Error updating project"], "error");
    }
  };

  useEffect(() => {
    void fetchData();
  }, [id]);

  if (loading) {
    return <Loader />;
  }

  if (error || !data) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    );
  }

  if (data.project && data.suppliers) {
    const project = data.project;
    const suppliers = data.suppliers;

    return (
      <MainLayout>
        <Formik<ProjectUpdateVars>
          onSubmit={onSubmit}
          initialValues={{
            id: project.id,
            input: {
              name: project.name,
              status: project.status,
              completed_responses_limit: project.completed_responses_limit || 0,
              account_id: project?.account?.id || account?.id,
              auto_purge_exempt: project?.auto_purge_exempt || false,
              suppliers: project.suppliers.map(
                (supplier) =>
                ({
                  id: supplier.id,
                  supplier_id: supplier.source.id,
                  limit: supplier.limit,
                  survey_url: supplier.survey_url,
                  opening_tag: supplier.opening_tag || "{{",
                  closing_tag: supplier.closing_tag || "}}",
                  respondent_key: supplier.respondent_key || "",
                  status_key: supplier.status_key || "",
                  questions: supplier.questions,
                  pre_survey_tool: supplier.pre_survey_tool || "",
                  ai_validation: supplier?.ai_validation || false,
                  pre_survey_id: supplier.pre_survey_id || "",
                  description: supplier.description || "",
                  score: supplier.score || "",
                  rt_proof_of_human_threshold: supplier.rt_proof_of_human_threshold ?? 60,
                  country_code: supplier.country_code || "", // The geo IP ISO-2 country code associated with the device
                  fields: supplier.fields.map((field) => ({
                    id: field.id,
                    name: field.name,
                    value: field.value,
                    required: field.required,
                    supplier_field_id: field.source?.id,
                  })),
                } as ProjectSupplierInput)
              ),
            },
          }}
          validationSchema={validationSchema}
        >
          {() => <Form suppliers={suppliers} projectManagers={projectManagers} />}
        </Formik>
      </MainLayout>
    );
  }

  return <Loader />;
};

export default ProjectEditRoute;
